import { IProductsWithVarietyItem } from '../../../../../../modules/API/Defaults/models/compliteProducts'
import { ISizeItem } from '../../../../../../modules/API/Products/models/size'
import { IPackagesItem } from '../../../../../../modules/API/Defaults/models/packages'

export interface IManualProductSelection {
    product: IProductsWithVarietyItem;
    size: ISizeItem | null;
    pack: IPackagesItem;
}

export interface IManualOrderRowKey {
    ProductID: number;
    VarietyID: number;
    SizeID: string;
}

export interface IManualDefaultPacks {
    isManualEntry: true;
    RecID: 0;
    ProductID: number;
    VarietyID: number;
    SizeID: string;
    PackID: number;
    Pack_Name: string;
    ProductDesc: string;
    VarietyDesc: string;
    ProductCalculationMethod: number;
    NoTagCollection: true;
    MandatoryWeight: boolean;
    CollectDeviation: 0;
    OrderPacks: 1;
    OrderWeight: 0;
    SupplyQty: 0;
    SupplyWeight: 0;
    ScanProblem: 0;
    ChangeReason: 0;
    GrowerID: 0;
    ImageUrl: '';
    Remarks: '';
}

export const buildManualDefaultPacks = (selection: IManualProductSelection): IManualDefaultPacks => {
    const { product, size, pack } = selection
    return {
        isManualEntry: true,
        RecID: 0,
        ProductID: product.ProductID,
        VarietyID: product.VarietyID,
        SizeID: size?.Description ?? '',
        PackID: pack.PackID,
        Pack_Name: pack.Pack_Name,
        ProductDesc: product.ProductDesc,
        VarietyDesc: product.VarietyDesc,
        ProductCalculationMethod: product.CalculationMethod,
        NoTagCollection: true,
        // real MandatoryWeight is unavailable for a hand-picked product — keep the
        // weight field editable so Weight:0 never reaches the server silently
        MandatoryWeight: true,
        CollectDeviation: 0,
        // 1, not 0: useLikutEntry divides OrderWeight/OrderPacks for the initial weight
        OrderPacks: 1,
        OrderWeight: 0,
        SupplyQty: 0,
        SupplyWeight: 0,
        ScanProblem: 0,
        ChangeReason: 0,
        GrowerID: 0,
        ImageUrl: '',
        Remarks: '',
    }
}

export const buildManualOrderRowKey = (selection: IManualProductSelection): IManualOrderRowKey => {
    return {
        ProductID: selection.product.ProductID,
        VarietyID: selection.product.VarietyID,
        SizeID: selection.size?.Description ?? '',
    }
}

export const findExactOrderRow = <T extends { ProductID: number; VarietyID: number; SizeID?: string | null }>(
    rows: T[] | null | undefined,
    key: IManualOrderRowKey,
): T | undefined => {
    if (!rows) return undefined
    return rows.find(row =>
        +row.ProductID === +key.ProductID &&
        +row.VarietyID === +key.VarietyID &&
        (row.SizeID?.trim() ?? '') === key.SizeID.trim()
    )
}

export const withNoTagCollection = <T extends object>(row: T): T & { NoTagCollection: true } => {
    return { ...row, NoTagCollection: true }
}

export interface IManualReplacementOrderRow {
    RecID: number | string;
    OrderPacks: number | string;
    OrderWeight: number | string;
    SupplyQty: number | string;
    SupplyWeight: number | string;
    CollectDeviation?: number | string | null;
    Remarks?: string | null;
}

export interface IManualReplacementPacks {
    RecID: number;
    ProductID: number;
    VarietyID: number;
    SizeID: string;
    PackID: number;
    Pack_Name: string;
    ProductDesc: string;
    VarietyDesc: string;
    ProductCalculationMethod: number;
    NoTagCollection: true;
    MandatoryWeight: boolean;
    CollectDeviation: number;
    OrderPacks: number;
    OrderWeight: number;
    SupplyQty: number;
    SupplyWeight: number;
    ScanProblem: 0;
    ChangeReason: 0;
    GrowerID: 0;
    ImageUrl: '';
    Remarks: string;
}

// Replacement flow: the pallet gets the hand-picked product, the replaced order
// row contributes only its RecID (OrderRowRecID) and the quantity base for the
// more/less-than-needed reason checks. Explicit literal on purpose — a spread of
// either argument would leak isManualEntry or the row's product identity.
export const buildManualReplacementPacks = (
    manualPacks: IManualDefaultPacks,
    orderRow: IManualReplacementOrderRow,
): IManualReplacementPacks => {
    return {
        RecID: Number(orderRow.RecID ?? 0),
        ProductID: manualPacks.ProductID,
        VarietyID: manualPacks.VarietyID,
        SizeID: manualPacks.SizeID,
        PackID: manualPacks.PackID,
        Pack_Name: manualPacks.Pack_Name,
        ProductDesc: manualPacks.ProductDesc,
        VarietyDesc: manualPacks.VarietyDesc,
        ProductCalculationMethod: manualPacks.ProductCalculationMethod,
        NoTagCollection: true,
        MandatoryWeight: manualPacks.MandatoryWeight,
        CollectDeviation: Number(orderRow.CollectDeviation ?? 0),
        OrderPacks: Number(orderRow.OrderPacks ?? 0),
        OrderWeight: Number(orderRow.OrderWeight ?? 0),
        SupplyQty: Number(orderRow.SupplyQty ?? 0),
        SupplyWeight: Number(orderRow.SupplyWeight ?? 0),
        ScanProblem: 0,
        ChangeReason: 0,
        GrowerID: 0,
        ImageUrl: '',
        Remarks: orderRow.Remarks ?? '',
    }
}
